import { useState, useEffect, useMemo } from "react";
import {
  getWarehouseRoadDistance,
  isWithinOwnDeliveryRecommendRadius,
  OWN_DELIVERY_RECOMMENDED_RADIUS_KM,
  type WarehouseDistanceAddress,
} from "../services/ownDeliveryDistance";

export type OwnDeliveryRecommendationAddress = WarehouseDistanceAddress;

export interface OwnDeliveryRecommendationResult {
  /** True when road distance from warehouse ≤ recommend radius */
  isRecommended: boolean;
  distanceKm: number | null;
  routeGeometry: number[][];
  destCoords: { lat: number; lon: number } | null;
  destLabel: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Reuses calculator distance stack (Geoapify geocode + road distance)
 * to decide whether Own Delivery should be preferred for this address.
 */
export function useOwnDeliveryRecommendation(
  address: OwnDeliveryRecommendationAddress | null | undefined,
): OwnDeliveryRecommendationResult {
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [routeGeometry, setRouteGeometry] = useState<number[][]>([]);
  const [destCoords, setDestCoords] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  const [destLabel, setDestLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const street = address?.street_name?.trim() || "";
  const house = address?.house_number?.trim() || "";
  const postal = address?.postal_code?.trim() || "";
  const city = address?.city?.trim() || "";
  const country = address?.country?.trim() || "";

  const addressKey = useMemo(
    () => [street, house, postal, city, country].join("|"),
    [street, house, postal, city, country],
  );

  useEffect(() => {
    let cancelled = false;

    async function compute() {
      if (!postal && !city) {
        setDistanceKm(null);
        setRouteGeometry([]);
        setDestCoords(null);
        setDestLabel(null);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await getWarehouseRoadDistance({
          street_name: street || undefined,
          house_number: house || undefined,
          postal_code: postal || undefined,
          city: city || undefined,
          country: country || undefined,
        });
        if (cancelled) return;
        if (!result) {
          setDistanceKm(null);
          setRouteGeometry([]);
          setDestCoords(null);
          setDestLabel(null);
          setError("Could not calculate route");
          return;
        }
        setDistanceKm(result.distanceKm);
        setRouteGeometry(result.routeGeometry);
        setDestCoords(result.destCoords);
        setDestLabel(result.destLabel);
      } catch (err) {
        console.error("useOwnDeliveryRecommendation error:", err);
        if (!cancelled) {
          setDistanceKm(null);
          setRouteGeometry([]);
          setDestCoords(null);
          setDestLabel(null);
          setError(
            err instanceof Error ? err.message : "Distance calculation failed",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    const timer = window.setTimeout(compute, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [addressKey, street, house, postal, city, country]);

  return {
    isRecommended: isWithinOwnDeliveryRecommendRadius(distanceKm),
    distanceKm,
    routeGeometry,
    destCoords,
    destLabel,
    loading,
    error,
  };
}

export { OWN_DELIVERY_RECOMMENDED_RADIUS_KM };
